import {compact} from 'lodash';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {Handler, Renderer} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisuallyHidden} from '../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useModalContext} from '../modals/modalContext';

/*
 * Props.
 */

export interface DialogHeaderProps {
  /** The dialog title. */
  title: ReactNode;
  /** Optional title icon. */
  titleIcon?: ReactNode;
  /** Called when the user requests for the dialog to be closed. */
  onRequestClose?: Handler;
  /** Called when the user requests for the dialog to be popped-out. */
  onRequestPopout?: Handler;
  /** Called when the user clicks the back button. If this is not supplied, no back button will render. */
  onBackClick?: Handler;
  /** Possibly render a tooltip info button in place of the close/pop-out button. */
  renderTooltip?: Renderer;
  /** Any custom buttons */
  customButtons?: ReactNode;
}

/*
 * Style.
 */

const StyledHeaderDiv = styled.div`
  flex-shrink: 0;
  padding: 4px;

  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  justify-items: center;

  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};
`;

const StyledTitleDiv = styled.div`
  grid-column: 2;
  max-width: 100%;
  padding: 12px 6px 11px 6px;

  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.large};

  ${ellipsis()}
`;

const StyledTitleContentDiv = styled.div`
  display: flex;
`;

const StyledTitleIconDiv = styled.div`
  margin-top: 2px;
  margin-left: 4px;
`;

const StyledButtonsDiv = styled.div`
  grid-column: 3;

  display: grid;
  grid-auto-flow: column;
  grid-column-gap: 6px;
  padding: 6px;
`;

const StyledLeftButtonWrapperDiv = styled(StyledButtonsDiv)`
  grid-column: 1;
`;

const StyledHiddenButtonsDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  opacity: 0;
  pointer-events: none;
`;

const StyledHeaderButton = styled(ChromelessButton)`
  padding: 5px;

  border-radius: 8px;
  color: ${(p) => p.theme.greys.shade80};

  &:hover {
    background-color: ${(p) => p.theme.alphas.gray10};
    color: ${(p) => p.theme.greys.shade80};
  }
`;

const StyledHeaderInfoButton = styled.div`
  padding: 5px;
  margin-right: 5px;
`;

/*
 * Component.
 */

export const DialogHeader: FC<DialogHeaderProps> = (props) => {
  const {customButtons, onBackClick} = props;
  const labelProps = useModalContext().getLabelProps();

  return (
    <StyledHeaderDiv>
      <StyledLeftButtonWrapperDiv>
        {maybeRenderBackButton(onBackClick)}
        <StyledHiddenButtonsDiv aria-hidden>
          {renderButtons(props, true)}
          {customButtons}
        </StyledHiddenButtonsDiv>
      </StyledLeftButtonWrapperDiv>
      <StyledTitleDiv {...labelProps}>
        <StyledTitleContentDiv>
          {props.title}
          <StyledTitleIconDiv>{props.titleIcon}</StyledTitleIconDiv>
        </StyledTitleContentDiv>
      </StyledTitleDiv>
      <StyledButtonsDiv>
        {renderButtons(props)}
        {customButtons}
      </StyledButtonsDiv>
    </StyledHeaderDiv>
  );
};

/*
 * Helpers.
 */

function maybeRenderBackButton(onBackClick?: Handler) {
  if (!onBackClick) {
    return null;
  }
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledHeaderButton key="back" onClick={onBackClick}>
          <Icon name="arrowBack" color={theme.greys.shade70} />
          <VisuallyHidden>
            <Trans id="dialogheader-back">Back</Trans>
          </VisuallyHidden>
        </StyledHeaderButton>
      )}
    </UserThemeConsumer>
  );
}

function renderButtons(props: DialogHeaderProps, isHidden: boolean = false) {
  const {onRequestPopout, onRequestClose, renderTooltip} = props;

  const popoutButtonRender = onRequestPopout && (
    <StyledHeaderButton key="popout" onClick={onRequestPopout}>
      <Icon name="popout" />
      <VisuallyHidden>
        <Trans id="dialogheader-popout">Popout</Trans>
      </VisuallyHidden>
    </StyledHeaderButton>
  );

  const closeButtonRender = onRequestClose && (
    <StyledHeaderButton
      key="close"
      onClick={onRequestClose}
      interactionId={!isHidden ? 'dialogHeaderClose' : undefined}
    >
      <Icon name="close" />
      <VisuallyHidden>
        <Trans id="dialogheader-close">Close</Trans>
      </VisuallyHidden>
    </StyledHeaderButton>
  );

  const infoTooltipButtonRender = renderTooltip && (
    <StyledHeaderInfoButton key="info">
      <TooltipCoordinator render={renderTooltip} placement="bottom" isMultiline>
        <Icon name="infoSmall" />
      </TooltipCoordinator>
    </StyledHeaderInfoButton>
  );

  return compact([popoutButtonRender, closeButtonRender, infoTooltipButtonRender]);
}
